import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, IconButton, CircularProgress, Divider,
  Avatar, Tooltip, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Radio, RadioGroup, FormControlLabel, FormControl, FormLabel,
  TextField, Snackbar, Alert, Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import FlagIcon from '@mui/icons-material/Flag';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/th';
import EventMediaCarousel from './EventMediaCarousel';
import ReviewMediaCarousel from './ReviewMediaCarousel';
import DualGroupChat from '../common/DualGroupChat';
import EventService from '../../services/eventService';

let useAuth;
try {
  const authModule = require('../Login/AuthContext');
  useAuth = authModule.useAuth;
} catch (e) {
  useAuth = () => ({ isAuthenticated: false, currentUser: null });
}

const API_URL = process.env.REACT_APP_API_URL;

const REPORT_REASONS = [
  { value: 'inappropriate', label: 'เนื้อหาไม่เหมาะสม' },
  { value: 'false_info', label: 'ข้อมูลเท็จหรือหลอกลวง' },
  { value: 'spam', label: 'สแปม / โฆษณาไม่พึงประสงค์' },
  { value: 'copyright', label: 'ละเมิดทรัพย์สินทางปัญญา' },
  { value: 'other', label: 'อื่นๆ' },
];

const EventExpandedView = ({ place, onClose }) => {
  const { isAuthenticated } = useAuth();
  const [fullData, setFullData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Participation state
  const [participationStatus, setParticipationStatus] = useState(null);
  const [participationLoading, setParticipationLoading] = useState(false);

  // Share menu
  const [shareAnchor, setShareAnchor] = useState(null);

  // Comment dialog
  const [commentOpen, setCommentOpen] = useState(false);

  // Report dialog
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDesc, setReportDesc] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const rawId = place.event_id || place.id;
  const isReview = place._source === 'review' || String(rawId).startsWith('review_');
  const numericId = isReview
    ? String(rawId).replace('review_', '')
    : String(rawId);
  const chatEventId = parseInt(numericId, 10);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchFull = async () => {
      try {
        setLoading(true);
        let data;
        if (isReview) {
          const res = await axios.get(`${API_URL}/reviews/${numericId}`);
          const r = res.data;
          data = {
            ...r,
            _source: 'review',
            event_name: r.place_name_th || r.place_name_en || r.place_name || 'Review',
            event_date: r.created_at,
            event_end_date: null,
            event_image_link: r.main_image_url,
            event_description: r.review_content_th || r.review_content_en || r.review_content || '',
            event_location: r.location_name || r.place_name || '',
          };
        } else {
          const res = await axios.get(`${API_URL}/events/${numericId}`);
          data = res.data;
        }
        if (!cancelled) setFullData(data);
      } catch (err) {
        console.error('[EventExpandedView] fetch error:', err);
        if (!cancelled) setFullData(place);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFull();
    return () => { cancelled = true; };
  }, [numericId, isReview, place]);

  const data = fullData || place;

  const eventName = data.event_name || data.name || 'Untitled';
  const eventDate = data.event_date || data.start_date;
  const eventEndDate = data.event_end_date || data.end_date;
  const eventLocation = data.event_location || data.location;
  const eventDescription = data.event_description || data.description || '';
  const website = data.website_link || data.website;
  const creatorName = data.organizer || data.creator_name || '';
  const avatarLetter = creatorName ? creatorName.charAt(0).toUpperCase() : '?';

  const allImages = [
    data.event_image_link,
    data.image,
    data.image_url,
    data.main_image_url,
    ...(data.additional_image_links || []),
  ].filter(Boolean);

  const hasReviewMedia = isReview && Array.isArray(data.media_items) && data.media_items.length >= 1;

  const lat = parseFloat(data.latitude);
  const lon = parseFloat(data.longitude);
  const mapSrc =
    !isNaN(lat) && !isNaN(lon)
      ? `https://maps.google.com/maps?q=${lat},${lon}&z=15&output=embed`
      : '';

  const displayDate = (d) => {
    if (!d) return '';
    moment.locale('th');
    return moment(d).format('DD MMM YY');
  };

  // Participation toggle
  const handleParticipate = useCallback(async (targetStatus) => {
    if (!isAuthenticated || participationLoading) return;
    setParticipationLoading(true);
    try {
      const newStatus = participationStatus === targetStatus ? 'not-going' : targetStatus;
      await EventService.updateParticipationStatus(parseInt(numericId, 10), newStatus, getAuthHeaders);
      setParticipationStatus(newStatus === 'not-going' ? null : newStatus);
      setSnack({
        open: true,
        msg: newStatus === 'interested' ? 'บันทึก: สนใจ' : newStatus === 'going' ? 'บันทึก: จะไป' : 'ยกเลิกแล้ว',
        severity: 'success',
      });
    } catch (err) {
      console.error('[EventExpandedView] participation error:', err);
    } finally {
      setParticipationLoading(false);
    }
  }, [isAuthenticated, participationLoading, participationStatus, numericId, getAuthHeaders]);

  // Share
  const handleShareTo = useCallback((platform) => {
    const eventUrl = `${window.location.origin}/event/${rawId}`;
    const shareTitle = eventName;
    let url = '';
    switch (platform) {
      case 'facebook': url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}`; break;
      case 'twitter': url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(eventUrl)}&text=${encodeURIComponent(shareTitle)}`; break;
      case 'line': url = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(eventUrl)}`; break;
      case 'whatsapp': url = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareTitle + ' ' + eventUrl)}`; break;
      case 'copy':
        navigator.clipboard.writeText(eventUrl).then(() =>
          setSnack({ open: true, msg: 'คัดลอกลิงก์แล้ว', severity: 'success' })
        );
        break;
      default: break;
    }
    if (url) window.open(url, '_blank', 'width=600,height=400');
    setShareAnchor(null);
  }, [rawId, eventName]);

  // Report submit
  const handleReportSubmit = useCallback(async () => {
    if (!reportReason) return;
    setReportSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/reports`,
        {
          item_type: isReview ? 'review' : 'event',
          item_id: parseInt(numericId, 10),
          reason: reportReason,
          description: reportDesc,
        },
        getAuthHeaders()
      );
      setReportOpen(false);
      setReportReason('');
      setReportDesc('');
      setSnack({ open: true, msg: 'รายงานถูกส่งแล้ว ขอบคุณสำหรับการแจ้ง', severity: 'success' });
    } catch (err) {
      console.error('[EventExpandedView] report error:', err);
      setSnack({ open: true, msg: 'ไม่สามารถส่งรายงานได้ กรุณาลองใหม่', severity: 'error' });
    } finally {
      setReportSubmitting(false);
    }
  }, [reportReason, reportDesc, isReview, numericId, getAuthHeaders]);

  const SocialBar = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 0.75,
        gap: 0.25,
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'grey.100',
        my: 1,
      }}
    >
      <Tooltip title="See creator's profile">
        <Avatar sx={{ width: 26, height: 26, fontSize: '0.7rem', bgcolor: 'primary.light', color: 'primary.contrastText', cursor: 'pointer', mr: 0.25 }}>
          {avatarLetter}
        </Avatar>
      </Tooltip>

      <Tooltip title="Follow this creator">
        <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }}>
          <PersonAddOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Box sx={{ flex: 1 }} />

      <Tooltip title="Comments">
        <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }} onClick={() => setCommentOpen(true)}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {isAuthenticated && (
        <Tooltip title={participationStatus === 'interested' ? 'Remove from favourites' : 'Add to favourite'}>
          <IconButton size="small" sx={{ p: 0.5, color: participationStatus === 'interested' ? 'warning.main' : 'text.secondary' }} onClick={() => handleParticipate('interested')} disabled={participationLoading}>
            {participationStatus === 'interested' ? <StarIcon sx={{ fontSize: 18 }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      )}

      {isAuthenticated && (
        <Tooltip title={participationStatus === 'going' ? 'Remove from going list' : 'Add to going list'}>
          <IconButton size="small" sx={{ p: 0.5, color: participationStatus === 'going' ? 'success.main' : 'text.secondary' }} onClick={() => handleParticipate('going')} disabled={participationLoading}>
            {participationStatus === 'going' ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : <EventAvailableIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Share">
        <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }} onClick={(e) => setShareAnchor(e.currentTarget)}>
          <ShareIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {isAuthenticated && (
        <Tooltip title="Report">
          <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }} onClick={() => setReportOpen(true)}>
            <ReportProblemOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 50,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 0,
        }}
      >
        {/* Sticky header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            noWrap
            sx={{ flex: 1, ml: 1, fontSize: '0.95rem' }}
          >
            {eventName}
          </Typography>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {loading && !fullData && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {/* Full-height image */}
          {hasReviewMedia ? (
            <ReviewMediaCarousel
              mediaItems={data.media_items}
              altText={eventName}
              fallbackImage={allImages[0]}
              height={300}
            />
          ) : allImages.length > 1 ? (
            <EventMediaCarousel images={allImages} altText={eventName} height={300} />
          ) : allImages.length === 1 ? (
            <Box
              component="img"
              src={allImages[0]}
              alt={eventName}
              sx={{ width: '100%', height: 'auto', display: 'block' }}
            />
          ) : null}

          <Box sx={{ px: 2, pt: 1.5, pb: 3 }}>
            {/* Title */}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, lineHeight: 1.3 }}>
              {eventName}
            </Typography>

            {/* Dates */}
            {(eventDate || eventEndDate) && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mb: 1.5,
                  p: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                {eventDate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <CalendarTodayIcon sx={{ fontSize: 15, color: 'primary.main', flexShrink: 0 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                        {isReview ? 'วันที่โพสต์' : 'เริ่มต้น'}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {displayDate(eventDate)}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {eventDate && eventEndDate && (
                  <Divider orientation="vertical" flexItem />
                )}
                {eventEndDate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <CalendarTodayIcon sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                        สิ้นสุด
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {displayDate(eventEndDate)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Buttons row — for events: immediately below dates */}
            {!isReview && SocialBar}

            {/* Location */}
            {eventLocation && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                <LocationOnIcon sx={{ fontSize: 17, color: 'primary.main', mt: 0.15, flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  {eventLocation}
                </Typography>
              </Box>
            )}

            {/* Buttons row — for reviews: just above description */}
            {isReview && SocialBar}

            {/* Description */}
            {eventDescription ? (
              <Typography
                variant="body2"
                sx={{ mb: 1.5, lineHeight: 1.7, color: 'text.primary', whiteSpace: 'pre-line' }}
              >
                {eventDescription}
              </Typography>
            ) : null}

            {/* Website */}
            {website && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <LanguageIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
                <Typography
                  component="a"
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ color: 'primary.main', textDecoration: 'none', wordBreak: 'break-all' }}
                >
                  {website}
                </Typography>
              </Box>
            )}

            {/* Map */}
            {mapSrc && (
              <Box
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: 180,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <iframe
                  title="Event location"
                  src={mapSrc}
                  width="100%"
                  height="100%"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Share menu */}
      <Menu anchorEl={shareAnchor} open={Boolean(shareAnchor)} onClose={() => setShareAnchor(null)} PaperProps={{ sx: { minWidth: 160 } }}>
        <MenuItem onClick={() => handleShareTo('copy')} sx={{ gap: 1 }}><ContentCopyIcon sx={{ fontSize: 16 }} /> คัดลอกลิงก์</MenuItem>
        <MenuItem onClick={() => handleShareTo('facebook')} sx={{ gap: 1 }}>Facebook</MenuItem>
        <MenuItem onClick={() => handleShareTo('twitter')} sx={{ gap: 1 }}>Twitter / X</MenuItem>
        <MenuItem onClick={() => handleShareTo('line')} sx={{ gap: 1 }}>LINE</MenuItem>
        <MenuItem onClick={() => handleShareTo('whatsapp')} sx={{ gap: 1 }}>WhatsApp</MenuItem>
      </Menu>

      {/* Comment dialog */}
      <Dialog open={commentOpen} onClose={() => setCommentOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { height: '70vh', display: 'flex', flexDirection: 'column' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }} noWrap>{eventName}</Typography>
          <IconButton size="small" onClick={() => setCommentOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: 'hidden', p: 1 }}>
          {isAuthenticated ? (
            <DualGroupChat eventId={chatEventId} chatType="interested" title="Comments" isAuthenticated={isAuthenticated} getAuthHeaders={getAuthHeaders} userGroups={null} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography color="text.secondary" textAlign="center">กรุณาเข้าสู่ระบบเพื่อแสดงความคิดเห็น</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Report dialog */}
      <Dialog
        open={reportOpen}
        onClose={() => { setReportOpen(false); setReportReason(''); setReportDesc(''); }}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box sx={{ bgcolor: 'error.main', px: 3, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FlagIcon sx={{ color: 'white', fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: 'white', flex: 1, fontSize: '1rem' }}>
            รายงานโพสต์นี้
          </Typography>
          <IconButton size="small" onClick={() => setReportOpen(false)} sx={{ color: 'rgba(255,255,255,0.8)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            กรุณาระบุเหตุผลที่ต้องการรายงานโพสต์นี้ ทีมงานจะตรวจสอบและดำเนินการโดยเร็วที่สุด
          </Typography>
          <FormControl component="fieldset" fullWidth required>
            <FormLabel component="legend" sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5, color: 'text.primary', '&.Mui-focused': { color: 'error.main' } }}>
              เหตุผล
            </FormLabel>
            <RadioGroup value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
              {REPORT_REASONS.map((r) => (
                <FormControlLabel
                  key={r.value}
                  value={r.value}
                  control={<Radio size="small" color="error" />}
                  label={<Typography variant="body2">{r.label}</Typography>}
                  sx={{ mb: 0.25 }}
                />
              ))}
            </RadioGroup>
          </FormControl>
          <TextField
            fullWidth multiline rows={3} variant="outlined" size="small"
            label="รายละเอียดเพิ่มเติม (ไม่บังคับ)"
            value={reportDesc} onChange={(e) => setReportDesc(e.target.value)}
            sx={{ mt: 2 }} inputProps={{ maxLength: 500 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
            {reportDesc.length}/500
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => { setReportOpen(false); setReportReason(''); setReportDesc(''); }} sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            ยกเลิก
          </Button>
          <Button variant="contained" color="error" onClick={handleReportSubmit} disabled={!reportReason || reportSubmitting} sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
            {reportSubmitting ? 'กำลังส่ง…' : 'ส่งรายงาน'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EventExpandedView;
